import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { withTranslation } from '../Translator';
import Button from '../Button';
import Stack from '../Stack';
import Text from '../Text';
import { ModalView } from '../ModalView';
import Icon from '../Icon';
import Header from '../Header';

const FIRST_THRESHOLD_DAYS_TO_DOMAINS_CERT_EXPIRATION = 30;
const SECOND_THRESHOLD_DAYS_TO_DOMAINS_CERT_EXPIRATION = 7;
const DOMAINS_CERT_EXPIRATION_MODAL_CLOSED_PREFIX = 'pp_domains_certificate_expiration_modal';
const ADMINISTRATION_DOMAINS_URL = '/administration/account/domains';

const THRESHOLD_TYPES = {
  FIRST: 'FIRST',
  SECOND: 'SECOND',
  EXPIRED: 'EXPIRED',
};

const getModalClosureKey = threshold => `${DOMAINS_CERT_EXPIRATION_MODAL_CLOSED_PREFIX}_${threshold}`;

const getDismissedDomains = (threshold) => {
  try {
    const key = getModalClosureKey(threshold);
    const storedValue = localStorage.getItem(key);

    return storedValue ? JSON.parse(storedValue) : [];
  } catch (error) {
    return [];
  }
};

const isModalClosed = (threshold, domain) => {
  const dismissedDomains = getDismissedDomains(threshold);

  return dismissedDomains.includes(domain);
};

const setModalClosed = (threshold, domain) => {
  const dismissedDomains = getDismissedDomains(threshold);
  if (!dismissedDomains.includes(domain)) {
    dismissedDomains.push(domain);
    localStorage.setItem(getModalClosureKey(threshold), JSON.stringify(dismissedDomains));
  }
};

const clearModalClosed = (domain) => {
  Object.values(THRESHOLD_TYPES).forEach((threshold) => {
    const dismissedDomains = getDismissedDomains(threshold);
    const updatedDomains = dismissedDomains.filter(dismissedDomain => dismissedDomain !== domain);

    if (dismissedDomains.length !== updatedDomains.length) {
      localStorage.setItem(getModalClosureKey(threshold), JSON.stringify(updatedDomains));
    }
  });
};

const cleanupUnusedDomains = (activeDomainsSet) => {
  Object.values(THRESHOLD_TYPES).forEach((threshold) => {
    const dismissedDomains = getDismissedDomains(threshold);
    const updatedDomains = dismissedDomains.filter(domain => activeDomainsSet.has(domain));

    if (dismissedDomains.length !== updatedDomains.length) {
      if (updatedDomains.length === 0) {
        localStorage.removeItem(getModalClosureKey(threshold));
      } else {
        localStorage.setItem(getModalClosureKey(threshold), JSON.stringify(updatedDomains));
      }
    }
  });
};

const calculateDaysDifference = (end, currentDate) => {
  const now = currentDate ? moment(currentDate).utc().startOf('day') : moment().utc().startOf('day');
  const expires = moment(end).utc().startOf('day');

  return expires.diff(now, 'days');
};

const getThresholdType = (daysLeft) => {
  if (daysLeft <= 0) {
    return THRESHOLD_TYPES.EXPIRED;
  }

  if (daysLeft <= SECOND_THRESHOLD_DAYS_TO_DOMAINS_CERT_EXPIRATION) {
    return THRESHOLD_TYPES.SECOND;
  }

  if (daysLeft <= FIRST_THRESHOLD_DAYS_TO_DOMAINS_CERT_EXPIRATION) {
    return THRESHOLD_TYPES.FIRST;
  }

  return null;
};

const processDomainsWithCertificates = (domainsList, currentDate) => {
  if (!domainsList || !domainsList.length) {
    return [];
  }

  const activeDomainNames = new Set(domainsList.map(domain => domain.domain));
  cleanupUnusedDomains(activeDomainNames);

  return domainsList
    .filter(domain => domain.certificate && domain.certificate.expires)
    .map((domain) => {
      const daysToExpiration = calculateDaysDifference(domain.certificate.expires, currentDate);
      const thresholdType = getThresholdType(daysToExpiration);

      if (!thresholdType) {
        clearModalClosed(domain.domain);
      }

      return {
        domain: domain.domain,
        thresholdType,
      };
    })
    .filter(domain => domain.thresholdType !== null);
};

const shouldShowDomainsCertExpirationModal = (domainsList, currentDate = moment()) => {
  const domainsWithExpiringCerts = processDomainsWithCertificates(domainsList, currentDate);

  if (!domainsWithExpiringCerts.length) {
    return false;
  }

  const domainsByThreshold = {
    [THRESHOLD_TYPES.EXPIRED]: [],
    [THRESHOLD_TYPES.SECOND]: [],
    [THRESHOLD_TYPES.FIRST]: [],
  };

  domainsWithExpiringCerts.forEach((domain) => {
    domainsByThreshold[domain.thresholdType].push(domain);
  });

  // eslint-disable-next-line no-restricted-syntax
  for (const thresholdType of [THRESHOLD_TYPES.EXPIRED, THRESHOLD_TYPES.SECOND, THRESHOLD_TYPES.FIRST]) {
    const domainsInThreshold = domainsByThreshold[thresholdType];

    if (domainsInThreshold.length > 0) {
      const domainToShow = domainsInThreshold.find(domain => !isModalClosed(thresholdType, domain.domain));

      if (domainToShow) {
        return {
          thresholdType,
          domains: domainsInThreshold,
        };
      }
    }
  }

  return false;
};

function getRequestConfig({ jwt }) {
  return {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwt}`,
    },
  };
}

function useGetOrganizationDomainsList({ isOwner, jwt }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (isOwner) {
      fetch('/api/domains/v1', getRequestConfig({ jwt }))
        .then(resp => resp.json())
        .then((resp) => {
          setData(resp);
        })
        .catch(() => {});
    }
  }, [isOwner, jwt]);

  return data;
}

const DomainsCertificateExpirationModal = ({
  jwt,
  userRole,
  t,
  currentDate = moment(),
}) => {
  const [modalInfo, setModalInfo] = useState(null);
  const isOwner = userRole === 'OWNER';
  const domainsList = useGetOrganizationDomainsList({ isOwner, jwt });

  useEffect(() => {
    if (isOwner && domainsList) {
      const modalToShow = shouldShowDomainsCertExpirationModal(domainsList, currentDate);
      setModalInfo(modalToShow);
    }
  }, [domainsList, isOwner, currentDate, t]);

  if (!modalInfo) return null;

  const { thresholdType, domains } = modalInfo;

  const handleCloseModal = () => {
    domains.forEach((domain) => {
      setModalClosed(thresholdType, domain.domain);
    });
    setModalInfo(null);
  };

  const isExpired = thresholdType === THRESHOLD_TYPES.EXPIRED;
  const modalTitle = isExpired
    ? t('DomainsCertificateExpirationModal.expiredTitle')
    : t('DomainsCertificateExpirationModal.expiringTitle');
  const modalDescription = isExpired
    ? t('DomainsCertificateExpirationModal.expiredDescription')
    : t('DomainsCertificateExpirationModal.expiringDescription');
  const iconColor = isExpired ? 'red' : 'orange';

  return (
    <ModalView
      simple={{ withCloseButton: true }}
      onClose={handleCloseModal}
    >
      <Stack vertical verticalAlignment="center" horizontalAlignment="middle">
        <Stack.Item>
          <Icon name="bell" size="large" color={iconColor} />
        </Stack.Item>
        <Stack.Item>
          <Header type="small">
            {modalTitle}
          </Header>
        </Stack.Item>
        <Stack.Item fill>
          <Text align="center">
            {modalDescription}
          </Text>
        </Stack.Item>
        <Stack.Item>
          <Button
            text={t('DomainsCertificateExpirationModal.button')}
            onClick={() => {
              window.location.href = ADMINISTRATION_DOMAINS_URL;
              handleCloseModal();
            }}
          />
        </Stack.Item>
      </Stack>
    </ModalView>
  );
};

export {
  shouldShowDomainsCertExpirationModal,
  THRESHOLD_TYPES,
  processDomainsWithCertificates,
  getThresholdType,
  calculateDaysDifference,
  isModalClosed,
  setModalClosed,
  clearModalClosed,
  getModalClosureKey,
  cleanupUnusedDomains,
  getDismissedDomains,
  DomainsCertificateExpirationModal as PureComponent,
};
export default withTranslation('components')(DomainsCertificateExpirationModal);
